// Uma midia do totem na lista: previa (video mudo ou foto), tipo, dimensao
// real com a orientacao (em pe / deitada) e aviso quando esta deitada — o
// totem costuma ser vertical e a midia deitada perde as laterais no recorte.
// Switch de ativo, setas de ordem e remover: mesma mecanica do BannerCard.

import { AlertTriangle, ArrowDown, ArrowUp, Clapperboard, Image as ImageIcon, Trash2 } from "lucide-react";
import { useState } from "react";

import { Switch } from "../../../components/ui/switch";
import { bannerImageUrl } from "../lib/bannerImageUrl";

export default function TotemMidiaCard({
  midia, posicao, total, onToggle, onRemover, onMover,
}) {
  const inativo = !midia.enabled;
  const ehVideo = midia.media_type === "video";
  const src = bannerImageUrl(midia.image_url);
  const [dim, setDim] = useState(null); // { w, h }
  const deitada = dim && dim.w > dim.h;
  const orientacao = dim ? (deitada ? "deitada" : dim.w === dim.h ? "quadrada" : "em pé") : null;

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden" data-e2e="totem-midia">
      <div className={`relative w-full h-64 md:h-72 bg-black ${inativo ? "opacity-40" : ""}`}>
        {ehVideo ? (
          <video
            src={src}
            muted
            playsInline
            preload="metadata"
            controls
            onLoadedMetadata={(e) => setDim({ w: e.currentTarget.videoWidth, h: e.currentTarget.videoHeight })}
            className="w-full h-full object-contain"
          />
        ) : (
          <img
            src={src}
            alt={midia.title || `Mídia ${posicao + 1}`}
            loading="lazy"
            onLoad={(e) => setDim({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
            className="w-full h-full object-contain"
          />
        )}
        <span className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/70 text-white text-[11px] font-semibold">
          {ehVideo ? <Clapperboard className="w-3 h-3" /> : <ImageIcon className="w-3 h-3" />}
          {ehVideo ? "Vídeo" : "Foto"}
        </span>
        {dim && (
          <span className="absolute bottom-2 right-2 px-2 py-0.5 rounded-full bg-black/70 text-white text-[11px] font-medium tabular-nums" data-e2e="totem-midia-dim">
            {dim.w} × {dim.h} · {orientacao}
          </span>
        )}
        {inativo && (
          <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-black/70 text-white text-[11px] font-semibold">
            Pausado
          </span>
        )}
      </div>

      {deitada && (
        <p className="flex items-start gap-2 px-3 py-2 text-xs text-amber-700 dark:text-amber-300 bg-amber-500/10 border-b border-amber-500/20" data-e2e="totem-midia-aviso">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-px" />
          Mídia deitada: num totem em pé ela preenche a altura e perde as laterais. Se a tela for vertical, use 1080 × 1920 px.
        </p>
      )}

      <div className="flex items-center gap-3 p-3">
        <span className="text-xs font-semibold text-muted-foreground tabular-nums w-6">{posicao + 1}º</span>
        <div className="flex items-center gap-1">
          <BotaoOrdem icon={ArrowUp} label="Mover para cima" disabled={posicao === 0} onClick={() => onMover(midia, -1)} />
          <BotaoOrdem icon={ArrowDown} label="Mover para baixo" disabled={posicao === total - 1} onClick={() => onMover(midia, 1)} />
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <span className="text-xs text-muted-foreground">{midia.enabled ? "No ar" : "Pausado"}</span>
          <Switch checked={!!midia.enabled} onCheckedChange={() => onToggle(midia)} aria-label={midia.enabled ? "Pausar mídia" : "Ativar mídia"} />
        </div>
        <button
          type="button"
          onClick={() => onRemover(midia)}
          aria-label="Remover mídia"
          title="Remover mídia"
          className="w-9 h-9 rounded-lg flex items-center justify-center text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

function BotaoOrdem({ icon: Icon, label, disabled, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className="w-8 h-8 rounded-lg flex items-center justify-center text-muted-foreground hover:bg-muted disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
    >
      <Icon className="w-4 h-4" />
    </button>
  );
}
